import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Grid,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  ActionIcon,
  Flex,
  Divider,
  LoadingOverlay,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
// import { countries } from './countries';
import { countries } from '@manifest-cyber/types/dist/countryCode';
import Icon from '../../../components/Icon';
import { useAiPolicies, PolicyFormData } from './hooks/useAiPolicies';
import { useUpdateAiPolicies } from './hooks/useUpdateAiPolicies';
import styles from './AiPolicies.module.scss';
import { useFetchUserHasPermissions } from '../../../api/user/useFetchUserHasPermissions';

export const AiPoliciesPage: React.FC = () => {
  const { t } = useTranslation();
  const canUpdateAiPolicy = useFetchUserHasPermissions({
    requiredPermissions: ['update:ai-risk-policy'],
  });

  const { data: policiesData, isLoading, isError } = useAiPolicies();
  const updatePoliciesMutation = useUpdateAiPolicies();

  const [formData, setFormData] = useState<PolicyFormData>({
    outdatedModel: {
      threshold: {
        duration: 1,
        unit: 'years',
      },
      severity: 'critical',
    },
    recentlyCreated: {
      threshold: {
        duration: 1,
        unit: 'years',
      },
      severity: 'critical',
    },
    flaggedCountries: {
      countries: [],
      severity: '',
    },
    trustedOrganizations: {
      suppliers: [],
      severity: '',
    },
    missingDatasetSeverity: '',
    approvedLicenseSeverity: '',
    reviewLicenseSeverity: '',
    forbiddenLicenseSeverity: '',
  });

  useEffect(() => {
    if (policiesData?.success && policiesData.data) {
      setFormData(policiesData.data);
    }
  }, [policiesData]);

  const [newOrganization, setNewOrganization] = useState('');

  const timeUnits = [
    { value: 'days', label: t('page.aiPolicies.timeUnits.days') },
    { value: 'weeks', label: t('page.aiPolicies.timeUnits.weeks') },
    { value: 'months', label: t('page.aiPolicies.timeUnits.months') },
    { value: 'years', label: t('page.aiPolicies.timeUnits.years') },
  ];

  const severityOptions = [
    { value: 'info', label: t('page.aiPolicies.severityOptions.info') },
    { value: 'low', label: t('page.aiPolicies.severityOptions.low') },
    { value: 'medium', label: t('page.aiPolicies.severityOptions.medium') },
    { value: 'high', label: t('page.aiPolicies.severityOptions.high') },
    { value: 'critical', label: t('page.aiPolicies.severityOptions.critical') },
  ];

  const severityOptionsWithNone = [
    { value: 'none', label: t('page.aiPolicies.severityOptions.none') },
    ...severityOptions,
  ];

  const licenseSeverityOptions = [
    { value: 'info', label: t('page.aiPolicies.severityOptions.info') },
    { value: 'low', label: t('page.aiPolicies.severityOptions.low') },
    { value: 'medium', label: t('page.aiPolicies.severityOptions.medium') },
    { value: 'high', label: t('page.aiPolicies.severityOptions.high') },
    { value: 'critical', label: t('page.aiPolicies.severityOptions.critical') },
  ];

  const missingDatasetSeverityOptions = [
    { value: 'info', label: t('page.aiPolicies.severityOptions.info') },
    { value: 'low', label: t('page.aiPolicies.severityOptions.low') },
    { value: 'medium', label: t('page.aiPolicies.severityOptions.medium') },
    { value: 'high', label: t('page.aiPolicies.severityOptions.high') },
    { value: 'critical', label: t('page.aiPolicies.severityOptions.critical') },
  ];

  const handleSave = () => {
    updatePoliciesMutation.mutate(formData);
  };

  const addOrganization = () => {
    if (
      newOrganization.trim() &&
      !formData.trustedOrganizations.suppliers.includes(newOrganization.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        trustedOrganizations: {
          ...prev.trustedOrganizations,
          suppliers: [...prev.trustedOrganizations.suppliers, newOrganization.trim()],
        },
      }));
      setNewOrganization('');
    }
  };

  const removeOrganization = (orgToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      trustedOrganizations: {
        ...prev.trustedOrganizations,
        suppliers: prev.trustedOrganizations.suppliers.filter(
          (org) => org !== orgToRemove,
        ),
      },
    }));
  };

  if (isLoading) {
    return (
      <Container size="xl" py="md" style={{ position: 'relative', minHeight: '400px' }}>
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl" py="md">
        <Text color="red">{t('page.aiPolicies.errors.loadFailed')}</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Flex justify="space-between" align="center" mb="xl">
        <Title order={1}>{t('page.aiPolicies.title')}</Title>
        <Button
          onClick={handleSave}
          loading={updatePoliciesMutation.isLoading}
          disabled={updatePoliciesMutation.isLoading || !canUpdateAiPolicy}
        >
          {t('page.aiPolicies.save')}
        </Button>
      </Flex>

      <Divider mb={24} />

      <Stack spacing="xl">
        <Stack spacing="md">
          <Grid>
            <Grid.Col span={9}>
              <Flex direction="column" gap={8}>
                <TextComponent variant="headerMedium">
                  {t('page.aiPolicies.outdatedModel.title')}
                </TextComponent>
                <TextComponent variant="bodyMedium" color="muted">
                  {t('page.aiPolicies.outdatedModel.description')}
                </TextComponent>
              </Flex>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack spacing="sm">
                <TextComponent variant="bodyMedium">
                  {t('page.aiPolicies.alertSeverity')}
                </TextComponent>
                <Select
                  data={severityOptions}
                  value={formData.outdatedModel.severity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      outdatedModel: {
                        ...prev.outdatedModel,
                        severity: value || 'medium',
                      },
                    }))
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={8}>
              <Stack spacing="sm">
                <Text size="sm" weight={500}>
                  {t('page.aiPolicies.outdatedModel.maxTimeBetweenUpdates')}
                </Text>
                <Group spacing="sm">
                  <NumberInput
                    hideControls
                    value={formData.outdatedModel.threshold.duration}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        outdatedModel: {
                          ...prev.outdatedModel,
                          threshold: {
                            ...prev.outdatedModel.threshold,
                            duration: value || 1,
                          },
                        },
                      }))
                    }
                    w={75}
                    min={1}
                  />
                  <Select
                    data={timeUnits}
                    value={formData.outdatedModel.threshold.unit}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        outdatedModel: {
                          ...prev.outdatedModel,
                          threshold: {
                            ...prev.outdatedModel.threshold,
                            unit: value || 'months',
                          },
                        },
                      }))
                    }
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}></Grid.Col>
          </Grid>
        </Stack>

        <Divider mt={24} mb={24} />

        <Stack spacing="md">
          <Grid>
            <Grid.Col span={9}>
              <Flex direction="column" gap={8}>
                <TextComponent variant="headerMedium">
                  {t('page.aiPolicies.recentlyCreated.title')}
                </TextComponent>
                <TextComponent variant="bodyMedium" color="muted">
                  {t('page.aiPolicies.recentlyCreated.description')}
                </TextComponent>
              </Flex>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack spacing="sm">
                <TextComponent variant="bodyMedium">
                  {t('page.aiPolicies.alertSeverity')}
                </TextComponent>
                <Select
                  data={severityOptions}
                  value={formData.recentlyCreated.severity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      recentlyCreated: {
                        ...prev.recentlyCreated,
                        severity: value || 'medium',
                      },
                    }))
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={8}>
              <Stack spacing="sm">
                <Text size="sm" weight={500}>
                  {t('page.aiPolicies.recentlyCreated.minimumModelAge')}
                </Text>
                <Group spacing="sm">
                  <NumberInput
                    hideControls
                    value={formData.recentlyCreated.threshold.duration}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        recentlyCreated: {
                          ...prev.recentlyCreated,
                          threshold: {
                            ...prev.recentlyCreated.threshold,
                            duration: value || 0,
                          },
                        },
                      }))
                    }
                    w={75}
                    min={1}
                  />
                  <Select
                    data={timeUnits}
                    value={formData.recentlyCreated.threshold.unit}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        recentlyCreated: {
                          ...prev.recentlyCreated,
                          threshold: {
                            ...prev.recentlyCreated.threshold,
                            unit: value || 'months',
                          },
                        },
                      }))
                    }
                  />
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}></Grid.Col>
          </Grid>
        </Stack>

        <Divider mt={24} mb={24} />

        <Stack spacing="md">
          <Grid>
            <Grid.Col span={8}>
              <Flex direction="column" gap={8}>
                <TextComponent variant="headerMedium">
                  {t('page.aiPolicies.flaggedCountries.title')}
                </TextComponent>
                <TextComponent variant="bodyMedium" color="muted">
                  {t('page.aiPolicies.flaggedCountries.description')}
                </TextComponent>
              </Flex>
            </Grid.Col>
            <Grid.Col span={1}></Grid.Col>
            <Grid.Col span={3}>
              <Stack spacing="sm">
                <TextComponent variant="bodyMedium">
                  {t('page.aiPolicies.alertSeverity')}
                </TextComponent>
                <Select
                  data={severityOptions}
                  value={formData.flaggedCountries.severity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      flaggedCountries: {
                        ...prev.flaggedCountries,
                        severity: value || 'high',
                      },
                    }))
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={8}>
              <Stack spacing="sm">
                <Text size="sm" weight={500}>
                  {t('page.aiPolicies.flaggedCountries.flaggedCountriesLabel')}
                </Text>
                <MultiSelect
                  data={countries.map((country) => ({
                    value: country.alpha3,
                    label: country.name,
                  }))}
                  value={formData.flaggedCountries.countries}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      flaggedCountries: { ...prev.flaggedCountries, countries: value },
                    }))
                  }
                  searchable
                  placeholder={t('page.aiPolicies.flaggedCountries.searchPlaceholder')}
                />
              </Stack>
            </Grid.Col>
            <Grid.Col span={3}></Grid.Col>
          </Grid>
        </Stack>

        <Divider mt={24} mb={24} />

        <Stack spacing="md">
          <Grid>
            <Grid.Col span={9}>
              <Flex direction="column" gap={8}>
                <TextComponent variant="headerMedium">
                  {t('page.aiPolicies.trustedOrganizations.title')}
                </TextComponent>
                <TextComponent variant="bodyMedium" color="muted">
                  {t('page.aiPolicies.trustedOrganizations.description')}
                </TextComponent>
              </Flex>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack spacing="sm">
                <TextComponent variant="bodyMedium">
                  {t('page.aiPolicies.alertSeverity')}
                </TextComponent>
                <Select
                  data={severityOptionsWithNone}
                  value={formData.trustedOrganizations.severity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      trustedOrganizations: {
                        ...prev.trustedOrganizations,
                        severity: value || 'none',
                      },
                    }))
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={8}>
              <Stack spacing="sm">
                <Text size="sm" weight={500}>
                  {t('page.aiPolicies.trustedOrganizations.addOrganization')}
                </Text>
                <Group spacing="sm">
                  <TextInput
                    value={newOrganization}
                    onChange={(e) => setNewOrganization(e.currentTarget.value)}
                    placeholder={t(
                      'page.aiPolicies.trustedOrganizations.addOrganizationPlaceholder',
                    )}
                    icon={<Icon icon="search" size="1x" />}
                    style={{ flex: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addOrganization();
                      }
                    }}
                  />
                  <Button variant="filled" color="cyan" onClick={addOrganization}>
                    {t('page.aiPolicies.trustedOrganizations.add')}
                  </Button>
                </Group>

                <Stack spacing="xs" mt="md">
                  {formData.trustedOrganizations.suppliers.map((org) => (
                    <Group spacing="sm" key={org}>
                      <Icon icon="check" size="1x" classNames={styles.check} />
                      <Text size="sm">{org}</Text>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => removeOrganization(org)}
                      >
                        <Icon icon="xmark" size="1x" classNames={styles.xmark} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}></Grid.Col>
          </Grid>
        </Stack>

        <Divider mt={24} mb={24} />

        <Stack spacing="md">
          <Grid>
            <Grid.Col span={9}>
              <Flex direction="column" gap={8}>
                <Title order={3}>{t('page.aiPolicies.missingDataset.title')}</Title>
                <TextComponent variant="bodyMedium" color="muted">
                  {t('page.aiPolicies.missingDataset.description')}
                </TextComponent>
              </Flex>
            </Grid.Col>
            <Grid.Col span={3}>
              <Stack spacing="sm">
                <TextComponent variant="bodyMedium">
                  {t('page.aiPolicies.alertSeverity')}
                </TextComponent>
                <Select
                  data={missingDatasetSeverityOptions}
                  value={formData.missingDatasetSeverity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      missingDatasetSeverity: value as 'Low' | 'Medium' | 'High',
                    }))
                  }
                />
              </Stack>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={9}></Grid.Col>
            <Grid.Col span={3}></Grid.Col>
          </Grid>
        </Stack>

        <Divider mt={24} mb={24} />

        <Stack spacing="md">
          <Flex direction="column" gap={8}>
            <Title order={3}>{t('page.aiPolicies.licenseScanning.title')}</Title>
            <TextComponent variant="bodyMedium" color="muted">
              {t('page.aiPolicies.licenseScanning.description')}
            </TextComponent>
          </Flex>

          <Stack spacing="md">
            <Grid>
              <Grid.Col span={3}>
                <Group spacing="sm">
                  <Icon
                    icon="triangle-exclamation"
                    size="1x"
                    classNames={styles.warning}
                  />
                  <Text size="sm" weight={500}>
                    {t('page.aiPolicies.licenseScanning.reviewLicenses')}
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label={t('page.aiPolicies.severity')}
                  data={licenseSeverityOptions}
                  value={formData.reviewLicenseSeverity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      reviewLicenseSeverity: value as
                        | 'Info'
                        | 'Low'
                        | 'Medium'
                        | 'High'
                        | 'Critical',
                    }))
                  }
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={3}>
                <Group spacing="sm">
                  <Icon icon="ban" size="1x" classNames={styles.critical} />
                  <Text size="sm" weight={500}>
                    {t('page.aiPolicies.licenseScanning.forbiddenLicenses')}
                  </Text>
                </Group>
              </Grid.Col>
              <Grid.Col span={3}>
                <Select
                  label={t('page.aiPolicies.severity')}
                  data={licenseSeverityOptions}
                  value={formData.forbiddenLicenseSeverity}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      forbiddenLicenseSeverity: value as
                        | 'Info'
                        | 'Low'
                        | 'Medium'
                        | 'High'
                        | 'Critical',
                    }))
                  }
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};
